import {
  Injectable,
  OnDestroy,
  NgZone,
  signal,
  WritableSignal,
  inject,
  EnvironmentInjector,
  runInInjectionContext,
} from '@angular/core';
import {takeWhile} from 'rxjs/operators';

import {NbLayoutDirectionService, NbLayoutDirection} from '@nebular/theme';
import {TranslateService} from '@ngx-translate/core';
import {availableLanguages, frontendRoleType, IstrapiRole, subscriptionStatus} from '../../interfaces/apiCalls';
import {environment} from '../../../environments/environment';
import {isElectron} from '../../common/utils';
import {sendMessageSync} from '../../utils/electronHelpers';
import {FullSettingKey, IDocSetting, SettingID} from '../../shared-types/userconfigTypes';
import {parametriseSetting, Setting} from '../../shared-components/datatype-config/datatype-setting-utils';
import {FrontendConfig} from '@med_poc/types';
import {LoggerService} from '../../services/loggerService';
import { ApiService } from '../../services/api/api.service';
import { PrincipalInfo } from '@med_poc/types';

declare global {
  interface Window { debugMode1337?: boolean; }
}

declare global {
  interface Window { debugMode1337?: boolean; }
}

@Injectable()
export class StateService implements OnDestroy {
  oldSettings_: any;
  debugCounter = 0;


  platform: 'mobile' | 'desktop';
  browser: string;

  user: any;
  subscriptionStatus = subscriptionStatus.ACTIVE;
  userSettings: WritableSignal<{name: FullSettingKey, setting: Setting}> = signal({} as {name: FullSettingKey, setting: Setting});
  user$: WritableSignal<any> = signal(null);
  allowedPrincipals: string[] = [];
  selectedPrincipalId: string | null = null;
  private allowedPrincipalsFetchedAt = 0;
  private allowedPrincipalsPromise: Promise<string[]> | null = null;
  private static readonly ALLOWED_PRINCIPALS_TTL_MS = 6000;

  // local storage pinned user
  private static readonly PINNED_USER_KEY = 'pinnedUser';

  pinnedUser$: WritableSignal<any> = signal(null);
  private static readonly PINNED_PRINCIPAL_KEY = 'pinnedPrincipal'; // local storage key for the pinned principal (the doctor an assistant is currently working for)

  private environmentInjector = inject(EnvironmentInjector);

  roles: IstrapiRole[] = []; // filled by loadRoles()
  private rolesPromise: Promise<IstrapiRole[]> | null = null; // in-flight roles request, shared by all callers
  professionalRoles: frontendRoleType[]; // only the role names which are considered to be professionals, defined by the constructor of the API service
  debugMode: boolean = false; // trigger this by typing in browser console: window._debug()
  isElectron = isElectron(); // to check if we are in electron environment
  // redirectToAfterSignup: `/${string}`;
  defaultRedirectToAfterLogin: `/${string}` = '/home'; // default redirect after login
  
  loopInfoToLocalhostInterval: any; // the reference to the interval that sends information to the localhost (like auth tokens etc)
  oidccredentials: object | null = null; // store the OIDC credentials here for access by other components / services
  // webserver port
  listenerPort: number = 28080; // default port
  medicusUser: string | null = null; // subfolder name for authInfo

  allowedPrincipals$: WritableSignal<PrincipalInfo[]> = signal([]);
  selectedPrincipalId$: WritableSignal<string | null> = signal(null);
  private principalsLoaded = false;

  protected layouts: any = [
    {
      name: 'One Column',
      icon: 'nb-layout-default',
      id: 'one-column',
      selected: true,
    },
    {
      name: 'Two Column',
      icon: 'nb-layout-two-column',
      id: 'two-column',
    },
    {
      name: 'Center Column',
      icon: 'nb-layout-centre',
      id: 'center-column',
    },
  ];
  debugModeSignal = signal(window['debugMode1337'] === true || localStorage.getItem('debugMode1337') === 'true');
  isDebugMode(): boolean {
    return window['debugMode1337'] === true || localStorage.getItem('debugMode1337') === 'true';
  }

  protected sidebars: any = [
    {
      name: 'Sidebar at layout start',
      icon: 'nb-layout-sidebar-left',
      id: 'start',
      selected: true,
    },
    {
      name: 'Sidebar at layout end',
      icon: 'nb-layout-sidebar-right',
      id: 'end',
    },
  ];

  alive = true;

  constructor(
    directionService: NbLayoutDirectionService,
    public translate: TranslateService,
    private zone: NgZone,
    private logger: LoggerService) {
  
      // initialise loop that calls the local rust webserver , passing down the user's JWT token and more (e.g. Open ID connect auth data from esante)
      this.loopInfoToLocalhost();
      this.loopInfoToLocalhostInterval = setInterval(async () => {
        await this.loopInfoToLocalhost();
      }, 2000);

  // for now, have 'settings' only in electron.
// TODO: fetch settings only if user is defined !
    if (isElectron()) {

      this.fetchAndSetUserSettings();

      setInterval(async () => {
        await this.fetchAndSetUserSettings();
      }, 5000);

    }

    directionService.onDirectionChange()
      .pipe(takeWhile(() => this.alive))
      .subscribe(direction => this.updateSidebarIcons(direction));

    this.updateSidebarIcons(directionService.getDirection());
    this.checkForBrowserAndPlattform();
    this.setLanguage();

    window['angularComponentReference'] = {
      zone: this.zone,
      componentFn: () => {
        this.debugMode = !this.debugMode;
        this.logger.debug('debug mode toggled to ' + this.debugMode);
      },
      component: this,
    };

    // set the debugMode state according to environment
    this.debugMode = (['dev', 'local'] as Array<FrontendConfig['environmentType']>)
      .includes(environment.environmentType);

    this.logger.debug('in electron? :', isElectron());
    this.hydratePinnedUser();
    this.selectedPrincipalId$.set(localStorage.getItem(StateService.PINNED_PRINCIPAL_KEY));
  }

  private hydratePinnedUser(): void {
    try {
      const raw = localStorage.getItem(StateService.PINNED_USER_KEY);
      if(raw)this.pinnedUser$.set(JSON.parse(raw));
    } catch (error) {
      this.logger.warn('Failed to hydrate pinned user from localStorage', error);
      localStorage.removeItem(StateService.PINNED_USER_KEY);
    }
  }

  // sets the language or uses english by default
  setLanguage(lang?: availableLanguages, opts?: { silent?: boolean }): void {
    if (lang) {
      this.translate.use(lang);
      // logged in users: language lives on the user record
      // logged out users: language lives in the local storage
      // localStorage.setItem('language', lang);
      if(!opts?.silent){
        window.location.reload();
      }
      return;
      // TODO: for gcloud bucket deployment, make language changes reload the full site,
      //  else things will lead to path errors.
      /*   if(environment.baseUrl == 'http://localhost:4200/'){
          window.location.reload();
        } else {
          console.log("reloading..")
          window.location.replace('https://storage.googleapis.com/med_poc_frontend/index.html')
        } */
    }
    const language = localStorage.getItem('language');
    if (language) {
      this.translate.use(language);
    } else {
      this.translate.use('en');
    }
  }

  ngOnDestroy() {
    this.alive = false;
  }


  setUser(user): void {
    this.user = user;
    // fetch user settings
    this.user$.set(user);
    // apply user language if it exists, otherwise use the current local storage
    if(user?.language){
      this.translate.use(user.language);
    }
    this.fetchAndSetUserSettings();
  }

  getUser(): any {
    return this.user;
  }

  amIaProfessional(): boolean {
    if (!this.user || !this.user.role) {
      return false;
    }
    return this.professionalRoles.includes(this.user.role.name.toLowerCase() as frontendRoleType);
  }

  setPinnedUser(user: any): void {
    this.pinnedUser$.set(user);
    if (user) localStorage.setItem(StateService.PINNED_USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(StateService.PINNED_USER_KEY);
  }

  getPinnedUser(): any {
    return this.pinnedUser$();
  }

  clearPinnedUser(): void {
    this.pinnedUser$.set(null);
    localStorage.removeItem(StateService.PINNED_USER_KEY);
  }


  private updateSidebarIcons(direction: NbLayoutDirection) {
    const [startSidebar, endSidebar] = this.sidebars;
    const isLtr = direction === NbLayoutDirection.LTR;
    const startIconClass = isLtr ? 'nb-layout-sidebar-left' : 'nb-layout-sidebar-right';
    const endIconClass = isLtr ? 'nb-layout-sidebar-right' : 'nb-layout-sidebar-left';
    startSidebar.icon = startIconClass;
    endSidebar.icon = endIconClass;
  }


  // WIP !
  // make async, as we might be fetching API calls here one day.
  async fetchAndSetUserSettings() {

    // do not do anything with settings if the user is not logged in.
    if (!this.user) {
      return;
    }

    // for now, we ask electron for the settings, later we store them in strapi.
    const rawSettings: { key: FullSettingKey, value: any } = await sendMessageSync<{
      key: FullSettingKey,
      value: any,
    }>('getAllUserSettings');
    // compare raw settings with oldSettings
    if (JSON.stringify(rawSettings) === JSON.stringify(this.oldSettings_)) {
      // this.logger.warn('settings have not changed');
      return;
    }
    this.oldSettings_ = rawSettings;

    this.logger.info('rawSettings:', rawSettings);

    const newSettings = {};

    // process every setting
    Object.keys(rawSettings).forEach(
      (key) => {
        const parametrisedSetting: IDocSetting<SettingID> = parametriseSetting(
          key as FullSettingKey,
          rawSettings[key],
        );

        // manually GC the old setting

        const oldSetting = this.userSettings()[key];
        if (oldSetting?.dispose) {
          oldSetting.dispose();
        }

        runInInjectionContext(this.environmentInjector, () => {
          newSettings[key] = new Setting(parametrisedSetting, ++this.debugCounter);
        });
      },
    );


    // @ts-ignore
    this.userSettings.set(newSettings);

    this.logger.info('Fully initialised user settings', this.userSettings());

  }

  // NOT IMPLEMENTED IN ELECTRON YET
  /*   async saveUserSettings(settings: userSettings) {
      // for now, we ask electron for the settings, later we store them in strapi.
      let returnString = await sendMessageSync('saveUserSettings', this.userSettings);
    } */

  checkForBrowserAndPlattform(): void {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      this.platform = 'mobile';
    } else {
      this.platform = 'desktop';
    }

    const agent = window.navigator.userAgent.toLowerCase();

    this.browser =
      agent.indexOf('edge') > -1 ? 'Microsoft Edge'
        : agent.indexOf('edg') > -1 ? 'Chromium-based Edge'
          : agent.indexOf('opr') > -1 ? 'Opera'
            : agent.indexOf('chrome') > -1 ? 'Chrome'
              : agent.indexOf('trident') > -1 ? 'Internet Explorer'
                : agent.indexOf('firefox') > -1 ? 'Firefox'
                  : agent.indexOf('safari') > -1 ? 'Safari'
                    : 'other';
  }

  wipe() {
    this.user = null;
    this.userSettings = null;
  }

  // stub for tests. when components use the state service but the tests do not require it yet
  stub() {
    return new Promise((resolve) => {
      resolve(true);
    });
  }

  setListenerPort(port: number): void {
    if (port > 0 && port <= 99999) {
      this.listenerPort = port;
      this.logger.info('Listener port updated to:', port);
    }
  }

  oidcStorageStatus: WritableSignal<'idle' | 'storing' | 'success'> = signal('idle');

 async loopInfoToLocalhost() {
    
      // if(!!this.user && this.oidccredentials){
      if(this.oidccredentials){
        this.oidcStorageStatus.set('storing')
        try {
          const response = await fetch(`http://127.0.0.1:${this.listenerPort}/ping`);
          const data = await response.json();
          if (response.status == 200) {
            console.devlog('GOT A POSITIVE PING !')

            console.devlog('Sending auth token data...')
            let auth_app_token;

            try{
              let formattedTokenInfo = JSON.parse(localStorage.getItem('auth_app_token'))
              // add Bearer prefix for easier processing later on
              formattedTokenInfo.value = `Bearer ${formattedTokenInfo.value}`
              auth_app_token = formattedTokenInfo
              // Build the authInfo URL - with or without medicusUser subfolder
            } catch{
              auth_app_token = { value: "Bearer eyJhbGXxxXXxXxxxxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxx"}
            }

            if (!auth_app_token || !auth_app_token.value) {
                auth_app_token = { value: "Bearer eyJhbGXxxXXxXxxxxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxxxxXxxxxxx"}
            }
            console.devlog("Finally, sending auth_app_token:", auth_app_token)
         
            const authInfoUrl = this.medicusUser
            ? `http://127.0.0.1:${this.listenerPort}/authInfo/${this.medicusUser}`
            : `http://127.0.0.1:${this.listenerPort}/authInfo`;
            
            const authResponse = await fetch(authInfoUrl, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ 'auth_app_token': JSON.stringify(auth_app_token),
                    'oidc_credentials': JSON.stringify(this.oidccredentials)
                   }),
                  });
                  
                  console.devlog('Auth response:', authResponse);
                  if(authResponse.status == 200){
                    console.devlog('Auth info sent successfully to localhost webserver.')
                    this.oidcStorageStatus.set('success')
                    clearInterval(this.loopInfoToLocalhostInterval);
                  }
          }
          // this.logger.info('127.0.0.1:28080:', data);
        // return data;
      } catch (error) {
        this.logger.error(`Error fetching from 127.0.0.1:${this.listenerPort}:`, error);
        throw error;
      }}
    }
    
  // if you are a professional, this will fetch the allowed principals for the user and automatically select the principal if there is only one available.
  // 'principals = people you work for'
  // note: must be triggered from outside to prevent circular references between api service and state service
    async getAllowedPrincipals(api: ApiService){
    try {
      const response = await api.getAllowedPrincipals();
      this.allowedPrincipals = response.data.data;


      // auth-select if it is only one principal
      if(this.allowedPrincipals.length === 1){
        //@ts-ignore FIX ME !
        this.selectedPrincipalId = this.allowedPrincipals[0].documentId;
      }
    } catch (error) {
      console.deverror("Error fetching allowed principals:", error)
    }
  }


  // fetch the allowed principals (the principals the current assistant is allowed to work for)
  async loadAllowedPrincipals(api: ApiService): Promise<void> {
    if (this.principalsLoaded) return;          // one fetch per session
    this.principalsLoaded = true;
    try {
      const principals = await api.loadAllowedPrincipals();
      this.allowedPrincipals$.set(principals);
      // drop a persisted doctor the user may no longer work for, and auto-select when there is only one
      this.setSelectedPrincipal(this.pickDefaultPrincipalId(principals));
    } catch (error) {
      this.principalsLoaded = false;            // allow a retry
      console.deverror('Error fetching allowed principals:', error);
    }
  }
  // fetch all strapi roles into `roles`. Concurrent callers share one request; `force` refetches even if roles are already loaded.
  // note: the api service is resolved lazily to prevent a circular dependency between api service and state service
  loadRoles(force = false): Promise<IstrapiRole[]> {
    if (!force && this.roles.length > 0) return Promise.resolve(this.roles);
    if (!this.rolesPromise) {
      const api = this.environmentInjector.get(ApiService);
      this.rolesPromise = (api.getAllRoles() as Promise<IstrapiRole[]>)
        .then((roles) => {
          this.roles = roles;
          return roles;
        })
        .finally(() => {
          this.rolesPromise = null;
        });
    }
    return this.rolesPromise;
  }

  setSelectedPrincipal(documentId: string | null): void {
    this.selectedPrincipalId$.set(documentId);
    if (documentId) localStorage.setItem(StateService.PINNED_PRINCIPAL_KEY, documentId);
    else localStorage.removeItem(StateService.PINNED_PRINCIPAL_KEY);
  }

  clearSelectedPrincipal(): void {
    this.setSelectedPrincipal(null);
  }

  // the doctor a form should start with: the pinned one if it is among `principals`, otherwise the only one available
  pickDefaultPrincipalId(principals: Array<{ documentId: string }>): string | null {
    const pinned = this.selectedPrincipalId$();
    if (pinned && principals.some(p => p.documentId === pinned)) return pinned; // return the pinned principal if it is among the available principals
    return principals.length === 1 ? principals[0].documentId : null; // else 
  }

  getSelectedPrincipal(): PrincipalInfo | null {
    const id = this.selectedPrincipalId$();
    return this.allowedPrincipals$().find(p => p.documentId === id) ?? null;
  }
}
